import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import Confetti from 'react-confetti';
import {
    GetStoredXfParticipantSession,
    SaveXfParticipantSession,
    GetXfPassportByFolio,
    GetXfPublicConfig,
    UpdateXfAdminConfig,
    SimulateXfStamp,
    SimulateXfStock,
    ResetXfParticipantDemo,
    CreateXfPassportHubConnection,
    DownloadXfRewardCardPng
} from '../../utilities/BL/XoloFest';

const DEMO_STATIONS = [
    {
        stationCode: 'P1_ACCESO',
        label: '🚪 Check-In Puerta 1 (Acceso)',
        shortLabel: 'Puerta 1',
        type: 'ACCESS',
        icon: 'bi-door-open-fill',
        color: '#10b981'
    },
    {
        stationCode: 'STAND_XOLOSHOP',
        label: '🛍️ Stand XoloShop (POS)',
        shortLabel: 'XoloShop',
        type: 'POS_STORE',
        amount: 350,
        icon: 'bi-bag-check-fill',
        color: '#f59e0b'
    },
    {
        stationCode: 'STAND_TACOS',
        label: '🌮 Tacos & Grill (POS)',
        shortLabel: 'Tacos & Grill',
        type: 'POS_STORE',
        amount: 220,
        icon: 'bi-cup-straw',
        color: '#f59e0b'
    },
    {
        stationCode: 'ACT_CALIENTE',
        label: '⚽ Tiro a Gol Caliente.mx (Activación)',
        shortLabel: 'Caliente.mx',
        type: 'ACTIVATION',
        activationResult: '¡GOLAZO AL ÁNGULO!',
        icon: 'bi-dribbble',
        color: '#dc2626'
    },
    {
        stationCode: 'ACT_NISSAN',
        label: '🚗 Reto Nissan (Activación)',
        shortLabel: 'Reto Nissan',
        type: 'ACTIVATION',
        activationResult: 'RETO COMPLETADO',
        icon: 'bi-speedometer2',
        color: '#dc2626'
    }
];

const formatTimeShort = (utcDateStr) => {
    if (!utcDateStr) return '';
    try {
        const d = new Date(utcDateStr);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('es-MX', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    } catch {
        return '';
    }
};

export const XoloFestPassport = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const folioParam = searchParams.get('folio') || '';
    const tokenParam = searchParams.get('t') || '';

    const [windowSize, setWindowSize] = useState({
        width: typeof window !== 'undefined' ? window.innerWidth : 400,
        height: typeof window !== 'undefined' ? window.innerHeight : 850
    });

    const [session, setSession] = useState(() => GetStoredXfParticipantSession());
    const [config, setConfig] = useState(() => GetStoredXfParticipantSession()?.config || null);
    const [loading, setLoading] = useState(false);
    const [simulating, setSimulating] = useState(false);
    const [downloadingPng, setDownloadingPng] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [liveBanner, setLiveBanner] = useState(null);
    const [showConfetti, setShowConfetti] = useState(false);
    const [signalrConnected, setSignalrConnected] = useState(false);
    const [showDemoTopBar, setShowDemoTopBar] = useState(true);
    const [demoBarOpen, setDemoBarOpen] = useState(false);
    const [countdownText, setCountdownText] = useState('');
    const [showQrModal, setShowQrModal] = useState(false);

    const prevRewardRef = useRef({
        hasEarnedReward: false,
        receivedDigitalBadge: false
    });

    useEffect(() => {
        const handleResize = () => {
            setWindowSize({
                width: window.innerWidth,
                height: window.innerHeight
            });
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const showToastNotification = useCallback((title, subtitle, tone = 'gold') => {
        setLiveBanner({ title, subtitle, tone, id: Date.now() });
    }, []);

    useEffect(() => {
        if (!liveBanner) return;
        const timer = setTimeout(() => {
            setLiveBanner(null);
        }, 5500);
        return () => clearTimeout(timer);
    }, [liveBanner]);

    const applyPassportViewResponse = useCallback((res) => {
        if (!res || (!res.participant && !res.ticketFolio)) return;

        if (res.config) {
            setConfig(res.config);
        }

        const updated = SaveXfParticipantSession(res);
        if (updated) {
            const p = updated.participant;
            const justUnlockedCoupon =
                p.hasEarnedReward && !prevRewardRef.current.hasEarnedReward;
            const justUnlockedBadge =
                p.receivedDigitalBadge && !prevRewardRef.current.receivedDigitalBadge;

            if (justUnlockedCoupon || justUnlockedBadge) {
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 7500);
            }

            prevRewardRef.current = {
                hasEarnedReward: Boolean(p.hasEarnedReward),
                receivedDigitalBadge: Boolean(p.receivedDigitalBadge)
            };

            setSession(updated);
        }
    }, []);

    const refreshPassportFromServer = useCallback(
        async (folioToFetch, tokenToFetch, silent = false) => {
            if (!folioToFetch) return;
            if (!silent) setLoading(true);
            setErrorMessage('');

            const res = await GetXfPassportByFolio(folioToFetch, tokenToFetch);
            if (!silent) setLoading(false);

            if (res.success && (res.participant || res.ticketFolio)) {
                applyPassportViewResponse(res);
            } else if (!GetStoredXfParticipantSession()) {
                setErrorMessage(
                    res.message || 'No se encontró un pasaporte activo para este folio.'
                );
            }
        },
        [applyPassportViewResponse]
    );

    useEffect(() => {
        let mounted = true;
        const init = async () => {
            const cfgRes = await GetXfPublicConfig();
            if (mounted && cfgRes.success && cfgRes.data) {
                setConfig((prev) => ({ ...(prev || {}), ...cfgRes.data }));
            }

            const stored = GetStoredXfParticipantSession();
            if (stored?.participant) {
                prevRewardRef.current = {
                    hasEarnedReward: Boolean(stored.participant.hasEarnedReward),
                    receivedDigitalBadge: Boolean(stored.participant.receivedDigitalBadge)
                };
            }

            const targetFolio = folioParam || stored?.participant?.ticketFolio || '';
            const targetToken =
                tokenParam ||
                stored?.participant?.verificationToken ||
                stored?.participantToken ||
                '';

            if (targetFolio) {
                await refreshPassportFromServer(targetFolio, targetToken);
            }
        };
        init();
        return () => {
            mounted = false;
        };
    }, [folioParam, tokenParam, refreshPassportFromServer]);

    // Conexión SignalR en Tiempo Real (/hub/xf)
    const activeFolio = session?.participant?.ticketFolio || folioParam || '';
    const activeToken =
        session?.participant?.verificationToken ||
        session?.participantToken ||
        tokenParam ||
        '';

    useEffect(() => {
        if (!activeFolio) return undefined;

        const hubInstance = CreateXfPassportHubConnection(activeFolio, {
            onConnectionStateChanged: (isConnected) => {
                setSignalrConnected(Boolean(isConnected));
            },
            onStampAdded: (payload) => {
                const stationLabel = payload?.stationName || payload?.stationCode || 'Estación Xolo Fest';
                showToastNotification(
                    `✨ ¡Nuevo Sello Estampado: ${stationLabel}!`,
                    payload?.actionName || 'Tu Pasaporte Digital se ha actualizado en tiempo real.',
                    'gold'
                );
                refreshPassportFromServer(activeFolio, activeToken, true);
            },
            onRewardUnlocked: (payload) => {
                const isBadge =
                    payload?.rewardType === 'DIGITAL_BADGE' ||
                    payload?.rewardStatus === 'DIGITAL_BADGE_EARNED';
                showToastNotification(
                    isBadge
                        ? '🎖️ ¡Insignia Leyenda Xolos 2026 Desbloqueada!'
                        : '🏆 ¡Cupón Dorado Oficial Desbloqueado!',
                    payload?.message || 'Has completado la meta del circuito Xolo Fest.',
                    isBadge ? 'crimson' : 'gold'
                );
                setShowConfetti(true);
                setTimeout(() => setShowConfetti(false), 7500);
                refreshPassportFromServer(activeFolio, activeToken, true);
            },
            onConfigUpdated: (payload) => {
                if (payload && typeof payload === 'object') {
                    setConfig((prev) => ({ ...(prev || {}), ...payload }));
                }
                refreshPassportFromServer(activeFolio, activeToken, true);
            },
            onParticipantReset: () => {
                prevRewardRef.current = {
                    hasEarnedReward: false,
                    receivedDigitalBadge: false
                };
                showToastNotification(
                    '🔄 Recorrido Reiniciado (Modo Demo)',
                    'El participante regresó al estado Pre-Evento (HasAttended = false).',
                    'neutral'
                );
                refreshPassportFromServer(activeFolio, activeToken, true);
            }
        });

        return () => {
            hubInstance.stop();
        };
    }, [activeFolio, activeToken, refreshPassportFromServer, showToastNotification]);

    // Cuenta regresiva al evento para el Modo Pre-Evento
    useEffect(() => {
        const updateCountdown = () => {
            const eventDateRaw = config?.eventDate || '2026-11-15T18:00:00Z';
            const target = new Date(eventDateRaw).getTime();
            const now = Date.now();
            const diff = target - now;

            if (isNaN(target) || diff <= 0) {
                setCountdownText('¡HOY ES EL GRAN DÍA EN EL ESTADIO CALIENTE!');
                return;
            }

            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
            const mins = Math.floor((diff / (1000 * 60)) % 60);
            setCountdownText(`Faltan ${days}d : ${hours}h : ${mins}m para abrir puertas`);
        };

        updateCountdown();
        const interval = setInterval(updateCountdown, 30000);
        return () => clearInterval(interval);
    }, [config?.eventDate]);

    // Acciones del Simulador Demo de Pasaporte (HU-03)
    const handleSimulateStationStamp = async (stationItem) => {
        if (!activeFolio || simulating) return;
        setSimulating(true);
        const res = await SimulateXfStamp({
            ticketFolio: activeFolio,
            stationCode: stationItem.stationCode,
            amount: stationItem.amount || null,
            activationResult: stationItem.activationResult || null
        });
        setSimulating(false);

        if (res.success) {
            applyPassportViewResponse(res);
            showToastNotification(
                `✅ Sello Simulado: ${stationItem.shortLabel}`,
                res.message || `Estación ${stationItem.stationCode} registrada con éxito.`,
                'gold'
            );
        } else {
            showToastNotification(
                '⚠️ Aviso del Simulador',
                res.message || 'No se pudo registrar el sello.',
                'crimson'
            );
        }
    };

    const handleToggleDisplayMode = async (newMode) => {
        if (simulating) return;
        setSimulating(true);
        const res = await UpdateXfAdminConfig({ passportDisplayMode: newMode });
        setSimulating(false);

        if (res.success) {
            if (res.data) setConfig((prev) => ({ ...(prev || {}), ...res.data }));
            await refreshPassportFromServer(activeFolio, activeToken, true);
            showToastNotification(
                newMode === 'REWARDS_PASSPORT'
                    ? '🏆 Modo Recompensas Gamificado Activo'
                    : '📖 Modo Bitácora Informativa Activo',
                'El Pasaporte Digital ha cambiado su presentación en vivo.',
                'gold'
            );
        }
    };

    const handleToggleUnlockRule = async (newRule) => {
        if (simulating) return;
        setSimulating(true);
        const res = await UpdateXfAdminConfig({ stampUnlockRule: newRule });
        setSimulating(false);

        if (res.success) {
            if (res.data) setConfig((prev) => ({ ...(prev || {}), ...res.data }));
            await refreshPassportFromServer(activeFolio, activeToken, true);
            showToastNotification(
                newRule === 'MANDATORY_ACTIVATION'
                    ? '🎯 Regla: Patrocinador Obligatorio'
                    : '🔓 Regla: Combinación Libre',
                'La regla de desbloqueo de recompensas se ha actualizado en vivo.',
                'gold'
            );
        }
    };

    const handleSimulateStockLevel = async (stockMode) => {
        if (simulating) return;
        setSimulating(true);
        const res = await SimulateXfStock(stockMode);
        setSimulating(false);

        if (res.success) {
            if (res.data) setConfig((prev) => ({ ...(prev || {}), ...res.data }));
            await refreshPassportFromServer(activeFolio, activeToken, true);
            const labelMap = {
                AVAILABLE: '📦 Stock Disponible Restaurado (0/300)',
                LAST_ONE: '⚠️ Simulación: Última Pieza Disponible (299/300)',
                EXHAUSTED: '🚫 Simulación: Stock Físico Agotado (300/300 -> Fallback Insignia)'
            };
            showToastNotification(
                labelMap[stockMode] || 'Inventario Actualizado',
                'Prueba completar los sellos para verificar la asignación transaccional.',
                stockMode === 'EXHAUSTED' ? 'crimson' : 'gold'
            );
        }
    };

    const handleResetDemoParticipant = async () => {
        if (!activeFolio || simulating) return;
        setSimulating(true);
        prevRewardRef.current = {
            hasEarnedReward: false,
            receivedDigitalBadge: false
        };
        const res = await ResetXfParticipantDemo(activeFolio);
        setSimulating(false);

        if (res.success) {
            applyPassportViewResponse(res);
            showToastNotification(
                '🔄 Recorrido Reiniciado a Pre-Evento',
                'Sellos y recompensas limpiados. Listo para una nueva demostración.',
                'neutral'
            );
        }
    };

    const handleDownloadCard = async (cardType) => {
        if (!session?.participant || downloadingPng) return;
        setDownloadingPng(true);
        await DownloadXfRewardCardPng(session.participant, config, cardType);
        setDownloadingPng(false);
    };

    const participant = session?.participant || null;
    const displayMode = config?.passportDisplayMode || 'REWARDS_PASSPORT';
    const unlockRule = config?.stampUnlockRule || 'ANY_COMBINATION';
    const rewardGoal = Number(config?.passportRewardGoal || 4);
    const stockLimit = Number(config?.rewardStockLimit || 300);
    const totalRedeemed = Number(config?.totalRedeemedCount || 0);
    const remainingStock =
        session?.remainingPhysicalStock !== undefined
            ? Number(session.remainingPhysicalStock)
            : Math.max(0, stockLimit - totalRedeemed);

    const stamps = Array.isArray(participant?.stamps) ? participant.stamps : [];
    const availableStations =
        Array.isArray(participant?.availableStations) && participant.availableStations.length > 0
            ? participant.availableStations
            : DEMO_STATIONS.map((st) => {
                  const matchingStamps = stamps.filter((s) => s.stationCode === st.stationCode);
                  return {
                      stationCode: st.stationCode,
                      stationName: st.label.replace(/^[^\s]+\s/, ''),
                      stationType: st.type,
                      isVisited: matchingStamps.length > 0,
                      visitsCount: matchingStamps.length,
                      lastVisitedAtUtc:
                          matchingStamps.length > 0
                              ? matchingStamps[matchingStamps.length - 1].timestampUtc
                              : null
                  };
              });

    const uniqueStampsCount = Number(
        participant?.uniqueStampsCount ??
            availableStations.filter((s) => s.isVisited).length
    );
    const progressPercent = Math.min(
        100,
        Math.round((uniqueStampsCount / Math.max(1, rewardGoal)) * 100)
    );
    const hasSponsorStamp = Boolean(
        participant?.hasSponsorActivationStamp ||
            availableStations.some((s) => s.stationType === 'ACTIVATION' && s.isVisited)
    );
    const missingMandatorySponsor = Boolean(
        participant?.missingMandatorySponsor ||
            (unlockRule === 'MANDATORY_ACTIVATION' &&
                uniqueStampsCount >= rewardGoal &&
                !hasSponsorStamp &&
                !participant?.hasEarnedReward &&
                !participant?.receivedDigitalBadge)
    );

    return (
        <div
            style={{
                minHeight: '100vh',
                background: "url('/img/page/backgrounds/Fondo-SelfieFan-Femenil.jpeg') center top / cover no-repeat fixed",
                color: '#f4f4f5',
                fontFamily: "'Montserrat', 'Inter', system-ui, sans-serif",
                paddingBottom: '4rem',
                position: 'relative',
                overflowX: 'hidden'
            }}
        >
            {showConfetti && (
                <Confetti
                    width={windowSize.width}
                    height={windowSize.height}
                    recycle={false}
                    numberOfPieces={340}
                    colors={['#f59e0b', '#fbbf24', '#dc2626', '#ffffff', '#10b981']}
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        zIndex: 9999,
                        pointerEvents: 'none'
                    }}
                />
            )}

            {/* Modal de QR Ampliado de Alto Contraste para Escaneo en Estadio */}
            <AnimatePresence>
                {showQrModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowQrModal(false)}
                        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
                        style={{
                            background: 'rgba(0, 0, 0, 0.88)',
                            backdropFilter: 'blur(8px)',
                            zIndex: 9999
                        }}
                    >
                        <motion.div
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                            className="p-4 rounded-4 text-center"
                            style={{
                                background: '#ffffff',
                                maxWidth: '350px',
                                width: '100%',
                                boxShadow: '0 25px 60px rgba(0,0,0,0.85)'
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <span
                                    className="fw-black text-uppercase"
                                    style={{
                                        fontSize: '0.8rem',
                                        color: '#dc2626',
                                        letterSpacing: 1.2
                                    }}
                                >
                                    Pase Oficial Xolo Fest
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setShowQrModal(false)}
                                    className="btn btn-sm btn-close"
                                    aria-label="Cerrar"
                                />
                            </div>

                            <div className="p-3 mb-2 d-inline-block rounded-3 border">
                                <QRCodeSVG
                                    value={
                                        participant?.verificationToken ||
                                        session?.participantToken ||
                                        participant?.ticketFolio ||
                                        ''
                                    }
                                    size={230}
                                    bgColor="#ffffff"
                                    fgColor="#09090b"
                                    level="H"
                                    includeMargin={false}
                                    imageSettings={{
                                        src: '/img/favicon/Big.png',
                                        height: 48,
                                        width: 48,
                                        excavate: true
                                    }}
                                />
                            </div>

                            <div
                                className="fw-black fs-4 mt-2"
                                style={{
                                    fontFamily: "'Courier New', monospace",
                                    color: '#09090b'
                                }}
                            >
                                {participant?.ticketFolio}
                            </div>
                            <div className="text-muted small fw-bold">
                                {participant?.fullName} • #{participant?.raffleTicketCode}
                            </div>
                            <small
                                className="text-secondary d-block mt-3"
                                style={{ fontSize: '0.72rem' }}
                            >
                                Toca fuera o presiona cerrar para volver
                            </small>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Barra Superior + 🛠️ Simulador Demo de Pasaporte (HU-03) */}
            {showDemoTopBar ? (
                <div
                    style={{
                        background: 'rgba(15, 15, 20, 0.96)',
                        borderBottom: '1px solid rgba(245, 158, 11, 0.35)',
                        backdropFilter: 'blur(12px)',
                        position: 'sticky',
                        top: 0,
                        zIndex: 1030
                    }}
                >
                    <div className="container" style={{ maxWidth: '580px' }}>
                        <div className="d-flex align-items-center justify-content-between py-2 gap-2 flex-wrap">
                            <div className="d-flex align-items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => navigate('/demo/xolofest')}
                                    className="btn btn-sm d-flex align-items-center gap-1"
                                    style={{
                                        background: 'rgba(255,255,255,0.06)',
                                        border: '1px solid rgba(255,255,255,0.16)',
                                        color: '#f4f4f5',
                                        fontSize: '0.75rem',
                                        fontWeight: 700
                                    }}
                                >
                                    <i className="bi bi-arrow-left text-warning"></i>
                                    Pre-Registro
                                </button>

                                <span
                                    className="badge rounded-pill d-inline-flex align-items-center gap-1 px-2 py-1"
                                    style={{
                                        background: signalrConnected
                                            ? 'rgba(16, 185, 129, 0.16)'
                                            : 'rgba(245, 158, 11, 0.16)',
                                        border: signalrConnected
                                            ? '1px solid rgba(16, 185, 129, 0.45)'
                                            : '1px solid rgba(245, 158, 11, 0.45)',
                                        color: signalrConnected ? '#6ee7b7' : '#fde68a',
                                        fontSize: '0.68rem',
                                        fontWeight: 700
                                    }}
                                >
                                    {loading
                                        ? '⏳ Actualizando...'
                                        : signalrConnected
                                        ? '🟢 En línea'
                                        : '🟡 Sincronizando...'}
                                </span>
                            </div>

                            <div className="d-flex align-items-center gap-2">
                                {participant && (
                                    <button
                                        type="button"
                                        onClick={() => setDemoBarOpen((prev) => !prev)}
                                        className="btn btn-sm d-flex align-items-center gap-1"
                                        style={{
                                            background: 'rgba(245, 158, 11, 0.18)',
                                            border: '1px solid rgba(245, 158, 11, 0.5)',
                                            color: '#fde68a',
                                            fontSize: '0.74rem',
                                            fontWeight: 800
                                        }}
                                    >
                                        <span>🛠️ Simulador Demo</span>
                                        <span>{demoBarOpen ? '▲' : '▼'}</span>
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={() => setShowDemoTopBar(false)}
                                    className="btn btn-sm py-1 px-2"
                                    style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        color: '#a1a1aa',
                                        fontSize: '0.72rem'
                                    }}
                                    title="Ocultar menú superior del demo"
                                >
                                    <i className="bi bi-eye-slash"></i>
                                </button>
                            </div>
                        </div>

                    {/* Panel Colapsable del Simulador Demo de Pasaporte */}
                    <AnimatePresence>
                        {participant && demoBarOpen && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                style={{ overflow: 'hidden' }}
                            >
                                <div
                                    className="p-3 mb-2 rounded-3"
                                    style={{
                                        background: 'rgba(24, 24, 27, 0.96)',
                                        border: '1px solid rgba(245, 158, 11, 0.35)'
                                    }}
                                >
                                    <div className="d-flex align-items-center justify-content-between mb-2">
                                        <span
                                            className="text-uppercase fw-bold"
                                            style={{
                                                color: '#fbbf24',
                                                fontSize: '0.7rem',
                                                letterSpacing: '0.8px'
                                            }}
                                        >
                                            1. Simular Check-In y Escaneo de Sellos en Estaciones
                                        </span>
                                        <button
                                            type="button"
                                            disabled={simulating}
                                            onClick={handleResetDemoParticipant}
                                            className="btn btn-sm py-0 px-2"
                                            style={{
                                                background: 'rgba(220, 38, 38, 0.18)',
                                                border: '1px solid rgba(220, 38, 38, 0.5)',
                                                color: '#fca5a5',
                                                fontSize: '0.68rem',
                                                fontWeight: 700
                                            }}
                                        >
                                            <i className="bi bi-arrow-counterclockwise me-1"></i>
                                            Reset Demo (Pre-Evento)
                                        </button>
                                    </div>

                                    <div className="d-flex flex-wrap gap-1 mb-3">
                                        {DEMO_STATIONS.map((st) => {
                                            const visited = availableStations.some(
                                                (a) => a.stationCode === st.stationCode && a.isVisited
                                            );
                                            return (
                                                <button
                                                    key={st.stationCode}
                                                    type="button"
                                                    disabled={simulating}
                                                    onClick={() => handleSimulateStationStamp(st)}
                                                    className="btn btn-sm flex-grow-1"
                                                    style={{
                                                        background: visited
                                                            ? 'rgba(16, 185, 129, 0.2)'
                                                            : 'rgba(255, 255, 255, 0.06)',
                                                        border: visited
                                                            ? '1px solid #10b981'
                                                            : `1px solid ${st.color}`,
                                                        color: '#ffffff',
                                                        fontSize: '0.7rem',
                                                        fontWeight: 700
                                                    }}
                                                >
                                                    {st.label}
                                                    {visited && ' ✓'}
                                                </button>
                                            );
                                        })}
                                    </div>

                                    {/* Conmutadores de Modo de Visualización y Regla de Desbloqueo */}
                                    <div className="row g-2 mb-2">
                                        <div className="col-12 col-sm-6">
                                            <div
                                                className="text-uppercase fw-bold mb-1"
                                                style={{ color: '#a1a1aa', fontSize: '0.66rem' }}
                                            >
                                                2. Modo Visual (PassportDisplayMode)
                                            </div>
                                            <div className="btn-group w-100" role="group">
                                                <button
                                                    type="button"
                                                    disabled={simulating}
                                                    onClick={() =>
                                                        handleToggleDisplayMode('REWARDS_PASSPORT')
                                                    }
                                                    className="btn btn-sm"
                                                    style={{
                                                        background:
                                                            displayMode === 'REWARDS_PASSPORT'
                                                                ? '#f59e0b'
                                                                : 'rgba(255,255,255,0.05)',
                                                        color:
                                                            displayMode === 'REWARDS_PASSPORT'
                                                                ? '#09090b'
                                                                : '#d4d4d8',
                                                        fontWeight: 800,
                                                        fontSize: '0.68rem',
                                                        border: '1px solid rgba(245,158,11,0.4)'
                                                    }}
                                                >
                                                    🏆 Recompensas
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={simulating}
                                                    onClick={() =>
                                                        handleToggleDisplayMode('INFORMATIVE_JOURNEY')
                                                    }
                                                    className="btn btn-sm"
                                                    style={{
                                                        background:
                                                            displayMode === 'INFORMATIVE_JOURNEY'
                                                                ? '#f59e0b'
                                                                : 'rgba(255,255,255,0.05)',
                                                        color:
                                                            displayMode === 'INFORMATIVE_JOURNEY'
                                                                ? '#09090b'
                                                                : '#d4d4d8',
                                                        fontWeight: 800,
                                                        fontSize: '0.68rem',
                                                        border: '1px solid rgba(245,158,11,0.4)'
                                                    }}
                                                >
                                                    📖 Bitácora
                                                </button>
                                            </div>
                                        </div>

                                        <div className="col-12 col-sm-6">
                                            <div
                                                className="text-uppercase fw-bold mb-1"
                                                style={{ color: '#a1a1aa', fontSize: '0.66rem' }}
                                            >
                                                3. Regla de Sellos (StampUnlockRule)
                                            </div>
                                            <div className="btn-group w-100" role="group">
                                                <button
                                                    type="button"
                                                    disabled={simulating}
                                                    onClick={() =>
                                                        handleToggleUnlockRule('ANY_COMBINATION')
                                                    }
                                                    className="btn btn-sm"
                                                    style={{
                                                        background:
                                                            unlockRule === 'ANY_COMBINATION'
                                                                ? '#10b981'
                                                                : 'rgba(255,255,255,0.05)',
                                                        color:
                                                            unlockRule === 'ANY_COMBINATION'
                                                                ? '#052e16'
                                                                : '#d4d4d8',
                                                        fontWeight: 800,
                                                        fontSize: '0.68rem',
                                                        border: '1px solid rgba(16,185,129,0.4)'
                                                    }}
                                                >
                                                    🔓 Libre
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={simulating}
                                                    onClick={() =>
                                                        handleToggleUnlockRule('MANDATORY_ACTIVATION')
                                                    }
                                                    className="btn btn-sm"
                                                    style={{
                                                        background:
                                                            unlockRule === 'MANDATORY_ACTIVATION'
                                                                ? '#dc2626'
                                                                : 'rgba(255,255,255,0.05)',
                                                        color: '#ffffff',
                                                        fontWeight: 800,
                                                        fontSize: '0.68rem',
                                                        border: '1px solid rgba(220,38,38,0.5)'
                                                    }}
                                                >
                                                    🎯 Patrocinador Oblig.
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Simulador de Stock Físico vs Fallback Insignia Digital */}
                                    <div>
                                        <div
                                            className="text-uppercase fw-bold mb-1"
                                            style={{ color: '#a1a1aa', fontSize: '0.66rem' }}
                                        >
                                            4. Simular Stock Físico de Souvenirs ({totalRedeemed}/
                                            {stockLimit} canjeados • Quedan {remainingStock})
                                        </div>
                                        <div className="d-flex flex-wrap gap-1">
                                            <button
                                                type="button"
                                                disabled={simulating}
                                                onClick={() => handleSimulateStockLevel('AVAILABLE')}
                                                className="btn btn-sm flex-grow-1"
                                                style={{
                                                    background: 'rgba(16, 185, 129, 0.14)',
                                                    border: '1px solid rgba(16, 185, 129, 0.45)',
                                                    color: '#6ee7b7',
                                                    fontSize: '0.68rem',
                                                    fontWeight: 700
                                                }}
                                            >
                                                📦 Stock Disponible (0/300)
                                            </button>
                                            <button
                                                type="button"
                                                disabled={simulating}
                                                onClick={() => handleSimulateStockLevel('LAST_ONE')}
                                                className="btn btn-sm flex-grow-1"
                                                style={{
                                                    background: 'rgba(245, 158, 11, 0.16)',
                                                    border: '1px solid rgba(245, 158, 11, 0.45)',
                                                    color: '#fde68a',
                                                    fontSize: '0.68rem',
                                                    fontWeight: 700
                                                }}
                                            >
                                                ⚠️ Última Pieza (299/300)
                                            </button>
                                            <button
                                                type="button"
                                                disabled={simulating}
                                                onClick={() => handleSimulateStockLevel('EXHAUSTED')}
                                                className="btn btn-sm flex-grow-1"
                                                style={{
                                                    background: 'rgba(220, 38, 38, 0.18)',
                                                    border: '1px solid rgba(220, 38, 38, 0.5)',
                                                    color: '#fca5a5',
                                                    fontSize: '0.68rem',
                                                    fontWeight: 700
                                                }}
                                            >
                                                🚫 Stock Agotado → Insignia (300/300)
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
            ) : (
                <button
                    type="button"
                    onClick={() => setShowDemoTopBar(true)}
                    className="btn btn-sm rounded-pill position-fixed top-0 end-0 m-2 px-3 py-1 shadow-lg"
                    style={{
                        zIndex: 1040,
                        background: 'rgba(24, 24, 27, 0.92)',
                        border: '1px solid rgba(245, 158, 11, 0.6)',
                        color: '#fde68a',
                        fontSize: '0.72rem',
                        backdropFilter: 'blur(8px)'
                    }}
                    title="Mostrar menú superior del demo"
                >
                    <i className="bi bi-tools me-1 text-warning"></i>
                    Modo Demo
                </button>
            )}

            <div className="container pt-3" style={{ maxWidth: '560px' }}>
                {/* Toast / Banner Animado de Notificación en Tiempo Real */}
                <AnimatePresence>
                    {liveBanner && (
                        <motion.div
                            key={liveBanner.id}
                            initial={{ opacity: 0, y: -15, scale: 0.96 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10 }}
                            className="p-3 rounded-4 mb-3 d-flex align-items-center justify-content-between gap-2"
                            style={{
                                background:
                                    liveBanner.tone === 'crimson'
                                        ? 'linear-gradient(135deg, rgba(153, 27, 27, 0.95) 0%, rgba(69, 10, 10, 0.98) 100%)'
                                        : liveBanner.tone === 'neutral'
                                        ? 'rgba(39, 39, 42, 0.96)'
                                        : 'linear-gradient(135deg, rgba(180, 83, 9, 0.95) 0%, rgba(24, 24, 27, 0.98) 100%)',
                                border: '1px solid #f59e0b',
                                boxShadow: '0 12px 30px rgba(0,0,0,0.65)'
                            }}
                        >
                            <div>
                                <div
                                    className="fw-bold text-white"
                                    style={{ fontSize: '0.88rem' }}
                                >
                                    {liveBanner.title}
                                </div>
                                <div style={{ color: '#fde68a', fontSize: '0.76rem' }}>
                                    {liveBanner.subtitle}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={() => setLiveBanner(null)}
                                className="btn btn-sm border-0 text-white"
                            >
                                <i className="bi bi-x-lg"></i>
                            </button>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Encabezado Principal */}
                <div className="text-center mb-3">
                    <span
                        className="badge rounded-pill px-3 py-1 mb-2"
                        style={{
                            background: 'rgba(245, 158, 11, 0.16)',
                            border: '1px solid rgba(245, 158, 11, 0.45)',
                            color: '#fde68a',
                            fontSize: '0.72rem',
                            fontWeight: 800,
                            letterSpacing: '1px'
                        }}
                    >
                        🛂 PASAPORTE DIGITAL PHYGITAL • CLUB TIJUANA
                    </span>
                    <h2
                        className="fw-black text-uppercase mb-0"
                        style={{
                            fontWeight: 900,
                            fontSize: '1.55rem',
                            color: '#ffffff'
                        }}
                    >
                        {config?.eventName || 'XOLO FEST 2026'}
                    </h2>
                </div>

                {/* SIN SESIÓN ACTIVA */}
                {!participant ? (
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="rounded-4 p-4 text-center"
                        style={{
                            background: 'rgba(24, 24, 27, 0.94)',
                            border: '1px solid rgba(220, 38, 38, 0.45)',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.75)'
                        }}
                    >
                        <div className="mb-3">
                            <i
                                className="bi bi-passport text-warning"
                                style={{ fontSize: '3.2rem' }}
                            ></i>
                        </div>
                        <h4 className="fw-bold text-white mb-2">
                            Aún no tienes un Pasaporte Activo
                        </h4>
                        <p style={{ color: '#a1a1aa', fontSize: '0.9rem' }}>
                            {errorMessage ||
                                'Registra tu boleto o recupera tu pase en el portal oficial para visualizar tu folio de la Tómbola Perrona y tu Pasaporte Digital.'}
                        </p>
                        <button
                            type="button"
                            onClick={() => navigate('/demo/xolofest')}
                            className="btn btn-lg fw-bold w-100 mt-2 py-3 rounded-3"
                            style={{
                                background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
                                border: '1px solid #f59e0b',
                                color: '#ffffff'
                            }}
                        >
                            🎰 Ir a Registro / Recuperación
                        </button>
                    </motion.div>
                ) : !participant.hasAttended ? (
                    /* =========================================================================
                       MODO 1: PRE-EVENTO (HasAttended === false)
                       ========================================================================= */
                    <motion.div
                        key="mode-pre-event"
                        initial={{ opacity: 0, scale: 0.97 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="rounded-4 p-4"
                        style={{
                            background:
                                'linear-gradient(160deg, rgba(24, 24, 27, 0.96) 0%, rgba(9, 9, 11, 0.98) 60%, rgba(69, 10, 10, 0.92) 100%)',
                            border: '2px solid #f59e0b',
                            boxShadow: '0 20px 50px rgba(0,0,0,0.85)'
                        }}
                    >
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                            <span
                                className="badge px-3 py-2 rounded-pill"
                                style={{
                                    background: 'rgba(245, 158, 11, 0.2)',
                                    border: '1px solid #f59e0b',
                                    color: '#fde68a',
                                    fontSize: '0.74rem',
                                    fontWeight: 800
                                }}
                            >
                                ⏳ MODO PRE-EVENTO — PENDIENTE DE CHECK-IN
                            </span>

                            <span
                                className="badge px-3 py-2 rounded-pill"
                                style={{
                                    background: '#dc2626',
                                    color: '#ffffff',
                                    fontSize: '0.74rem',
                                    fontWeight: 800
                                }}
                            >
                                {participant.ticketCategory || 'GENERAL'} • {participant.ticketFolio}
                            </span>
                        </div>

                        {/* Cuenta Regresiva */}
                        <div
                            className="p-2 px-3 rounded-3 mb-3 text-center"
                            style={{
                                background: 'rgba(255, 255, 255, 0.04)',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                color: '#fde68a',
                                fontSize: '0.8rem',
                                fontWeight: 700
                            }}
                        >
                            <i className="bi bi-clock-history me-2 text-warning"></i>
                            {countdownText}
                        </div>

                        <div className="text-center mb-3">
                            <div
                                className="text-uppercase"
                                style={{
                                    color: '#a1a1aa',
                                    fontSize: '0.72rem',
                                    letterSpacing: '1px'
                                }}
                            >
                                Titular del Pase Oficial
                            </div>
                            <h3 className="fw-bold text-white mb-0" style={{ fontSize: '1.35rem' }}>
                                {participant.fullName}
                            </h3>
                        </div>

                        {/* Tarjeta Oficial Tómbola Perrona */}
                        <div
                            className="p-3 rounded-4 mb-4 text-center"
                            style={{
                                background:
                                    'linear-gradient(135deg, rgba(245, 158, 11, 0.22) 0%, rgba(220, 38, 38, 0.26) 100%)',
                                border: '1px solid rgba(245, 158, 11, 0.65)'
                            }}
                        >
                            <div
                                className="text-uppercase fw-bold"
                                style={{
                                    color: '#fde68a',
                                    fontSize: '0.73rem',
                                    letterSpacing: '1.2px'
                                }}
                            >
                                🎰 INSCRIPCIÓN TÓMBOLA PERRONA CONFIRMADA
                            </div>
                            <div
                                style={{
                                    fontFamily: "'Courier New', monospace",
                                    fontSize: '1.95rem',
                                    fontWeight: 900,
                                    color: '#ffffff',
                                    letterSpacing: '2px'
                                }}
                            >
                                #{participant.raffleTicketCode}
                            </div>
                            <small style={{ color: '#e4e4e7', fontSize: '0.78rem' }}>
                                Presenta este código QR en las puertas del estadio para hacer Check-In y
                                activar tu recorrido interactivo.
                            </small>
                        </div>

                        {/* QR Oficial de Acceso */}
                        <div className="text-center mb-4">
                            <div
                                onClick={() => setShowQrModal(true)}
                                className="d-inline-block p-3 rounded-4"
                                style={{
                                    background: '#ffffff',
                                    border: '4px solid #f59e0b',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.55)',
                                    cursor: 'pointer'
                                }}
                                title="Toca para ampliar el código QR"
                            >
                                <QRCodeSVG
                                    value={
                                        participant.verificationToken ||
                                        session?.participantToken ||
                                        participant.ticketFolio
                                    }
                                    size={200}
                                    bgColor="#ffffff"
                                    fgColor="#09090b"
                                    level="H"
                                    includeMargin={true}
                                    imageSettings={{
                                        src: '/img/favicon/Big.png',
                                        height: 44,
                                        width: 44,
                                        excavate: true
                                    }}
                                />
                            </div>
                            <div className="mt-2 d-flex justify-content-center gap-2 align-items-center flex-wrap">
                                <button
                                    type="button"
                                    onClick={() => setShowQrModal(true)}
                                    className="btn btn-sm btn-outline-warning rounded-pill px-3 py-1"
                                    style={{ fontSize: '0.74rem', fontWeight: 700 }}
                                >
                                    <i className="bi bi-arrows-fullscreen me-1"></i>
                                    Ver QR Ampliado
                                </button>
                                <span
                                    className="badge rounded-pill px-3 py-1"
                                    style={{
                                        background: 'rgba(255,255,255,0.08)',
                                        color: '#e4e4e7',
                                        fontSize: '0.72rem'
                                    }}
                                >
                                    <i className="bi bi-qr-code-scan text-warning me-1"></i>
                                    Listo para escanear en Puerta 1
                                </span>
                            </div>
                        </div>

                        <div className="d-grid gap-2">
                            <button
                                type="button"
                                disabled={downloadingPng}
                                onClick={() => handleDownloadCard('RAFFLE_PASS')}
                                className="btn btn-lg fw-bold py-3 rounded-3"
                                style={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    color: '#09090b',
                                    border: 'none',
                                    fontSize: '0.95rem'
                                }}
                            >
                                <i className="bi bi-download me-2"></i>
                                {downloadingPng
                                    ? 'Generando Imagen PNG...'
                                    : '📥 Descargar Pase de Tómbola Perrona (PNG)'}
                            </button>

                            <button
                                type="button"
                                disabled={simulating}
                                onClick={() => handleSimulateStationStamp(DEMO_STATIONS[0])}
                                className="btn py-2 fw-bold rounded-3"
                                style={{
                                    background: 'rgba(16, 185, 129, 0.16)',
                                    border: '1px solid #10b981',
                                    color: '#6ee7b7',
                                    fontSize: '0.84rem'
                                }}
                            >
                                <i className="bi bi-door-open-fill me-1"></i>
                                Simular Check-In en Puerta Ahora (Activar Pasaporte)
                            </button>
                        </div>
                    </motion.div>
                ) : displayMode === 'INFORMATIVE_JOURNEY' ? (
                    /* =========================================================================
                       MODO 3: BITÁCORA INFORMATIVA (HasAttended === true & INFORMATIVE_JOURNEY)
                       ========================================================================= */
                    <motion.div
                        key="mode-informative-journey"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {/* Encabezado Conmemorativo Limpio */}
                        <div
                            className="rounded-4 p-4 mb-3"
                            style={{
                                background:
                                    'linear-gradient(160deg, rgba(24, 24, 27, 0.96) 0%, rgba(9, 9, 11, 0.98) 100%)',
                                border: '1px solid rgba(245, 158, 11, 0.5)',
                                boxShadow: '0 16px 40px rgba(0,0,0,0.75)'
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                <span
                                    className="badge px-3 py-2 rounded-pill"
                                    style={{
                                        background: 'rgba(16, 185, 129, 0.18)',
                                        border: '1px solid #10b981',
                                        color: '#6ee7b7',
                                        fontSize: '0.73rem',
                                        fontWeight: 800
                                    }}
                                >
                                    📖 BITÁCORA CONMEMORATIVA DE RECORRIDO
                                </span>
                                <span
                                    className="badge px-3 py-2 rounded-pill"
                                    style={{
                                        background: '#dc2626',
                                        color: '#ffffff',
                                        fontSize: '0.73rem',
                                        fontWeight: 800
                                    }}
                                >
                                    TÓMBOLA: #{participant.raffleTicketCode}
                                </span>
                            </div>

                            <div className="row align-items-center g-3">
                                <div className="col-12 col-sm-7 text-center text-sm-start">
                                    <h4 className="fw-bold text-white mb-1">{participant.fullName}</h4>
                                    <div
                                        style={{
                                            color: '#fde68a',
                                            fontSize: '0.82rem',
                                            fontFamily: 'monospace',
                                            fontWeight: 700
                                        }}
                                    >
                                        BOLETO: {participant.ticketFolio} ({participant.ticketCategory})
                                    </div>
                                    <p
                                        className="mt-2 mb-0"
                                        style={{ color: '#a1a1aa', fontSize: '0.8rem' }}
                                    >
                                        Consulta el historial oficial de zonas, experiencias y gastronomía
                                        que has disfrutado hoy en el Estadio Caliente.
                                    </p>
                                </div>
                                <div className="col-12 col-sm-5 text-center">
                                    <div
                                        onClick={() => setShowQrModal(true)}
                                        className="d-inline-block p-2 rounded-3"
                                        style={{
                                            background: '#ffffff',
                                            border: '3px solid #f59e0b',
                                            cursor: 'pointer'
                                        }}
                                        title="Toca para ampliar el código QR"
                                    >
                                        <QRCodeSVG
                                            value={
                                                participant.verificationToken ||
                                                participant.ticketFolio
                                            }
                                            size={115}
                                            bgColor="#ffffff"
                                            fgColor="#09090b"
                                            level="H"
                                            includeMargin={false}
                                            imageSettings={{
                                                src: '/img/favicon/Big.png',
                                                height: 26,
                                                width: 26,
                                                excavate: true
                                            }}
                                        />
                                    </div>
                                    <div className="mt-1">
                                        <button
                                            type="button"
                                            onClick={() => setShowQrModal(true)}
                                            className="btn btn-sm btn-outline-warning rounded-pill px-2 py-0"
                                            style={{ fontSize: '0.68rem', fontWeight: 700 }}
                                        >
                                            Ver QR
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Resumen de Actividad */}
                            <div className="row g-2 mt-3 pt-3 border-top border-secondary border-opacity-25 text-center">
                                <div className="col-4">
                                    <div
                                        className="p-2 rounded-3"
                                        style={{ background: 'rgba(255,255,255,0.04)' }}
                                    >
                                        <div
                                            className="fw-black text-warning"
                                            style={{ fontSize: '1.25rem', fontWeight: 900 }}
                                        >
                                            {uniqueStampsCount}
                                        </div>
                                        <small style={{ color: '#a1a1aa', fontSize: '0.7rem' }}>
                                            Estaciones
                                        </small>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div
                                        className="p-2 rounded-3"
                                        style={{ background: 'rgba(255,255,255,0.04)' }}
                                    >
                                        <div
                                            className="fw-black text-white"
                                            style={{ fontSize: '1.25rem', fontWeight: 900 }}
                                        >
                                            {participant.completedActivationsCount || 0}
                                        </div>
                                        <small style={{ color: '#a1a1aa', fontSize: '0.7rem' }}>
                                            Activaciones
                                        </small>
                                    </div>
                                </div>
                                <div className="col-4">
                                    <div
                                        className="p-2 rounded-3"
                                        style={{ background: 'rgba(255,255,255,0.04)' }}
                                    >
                                        <div
                                            className="fw-black text-success"
                                            style={{ fontSize: '1.25rem', fontWeight: 900 }}
                                        >
                                            {stamps.length}
                                        </div>
                                        <small style={{ color: '#a1a1aa', fontSize: '0.7rem' }}>
                                            Visitas Totales
                                        </small>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Línea de Tiempo Cronológica Detallada */}
                        <div
                            className="rounded-4 p-4"
                            style={{
                                background: 'rgba(24, 24, 27, 0.94)',
                                border: '1px solid rgba(255,255,255,0.14)'
                            }}
                        >
                            <h5 className="fw-bold text-white mb-3" style={{ fontSize: '1rem' }}>
                                <i className="bi bi-journal-check text-warning me-2"></i>
                                Línea de Tiempo de tu Recorrido ({stamps.length})
                            </h5>

                            {stamps.length === 0 ? (
                                <p className="text-secondary mb-0" style={{ fontSize: '0.85rem' }}>
                                    Aún no hay registros en tu bitácora. Escanea tu QR en cualquier
                                    estación.
                                </p>
                            ) : (
                                <div className="d-flex flex-column gap-2">
                                    {stamps.map((stamp, idx) => (
                                        <div
                                            key={stamp.id || idx}
                                            className="p-3 rounded-3 d-flex align-items-center justify-content-between gap-2"
                                            style={{
                                                background: 'rgba(9, 9, 11, 0.75)',
                                                borderLeft: '4px solid #f59e0b'
                                            }}
                                        >
                                            <div>
                                                <div
                                                    className="fw-bold text-white"
                                                    style={{ fontSize: '0.88rem' }}
                                                >
                                                    {stamp.stationName || stamp.stationCode}
                                                </div>
                                                <div
                                                    style={{
                                                        color: '#a1a1aa',
                                                        fontSize: '0.76rem'
                                                    }}
                                                >
                                                    {stamp.actionName || stamp.stationType}
                                                    {stamp.sponsorName
                                                        ? ` • Patrocinador: ${stamp.sponsorName}`
                                                        : ''}
                                                </div>
                                            </div>
                                            <span
                                                className="badge"
                                                style={{
                                                    background: 'rgba(245, 158, 11, 0.15)',
                                                    color: '#fde68a',
                                                    fontSize: '0.7rem',
                                                    fontFamily: 'monospace'
                                                }}
                                            >
                                                {formatTimeShort(stamp.timestampUtc)}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.div>
                ) : (
                    /* =========================================================================
                       MODO 2: RECOMPENSAS GAMIFICADO (HasAttended === true & REWARDS_PASSPORT)
                       ========================================================================= */
                    <motion.div
                        key="mode-rewards-passport"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        {/* 1. QR OFICIAL DEL AFICIONADO SIEMPRE ARRIBA Y DISPONIBLE */}
                        <div
                            className="rounded-4 p-3 mb-3 d-flex align-items-center justify-content-between flex-wrap gap-3"
                            style={{
                                background: 'rgba(24, 24, 27, 0.94)',
                                border: '1px solid rgba(245, 158, 11, 0.45)',
                                boxShadow: '0 10px 30px rgba(0,0,0,0.5)'
                            }}
                        >
                            <div className="d-flex align-items-center gap-3">
                                <div
                                    onClick={() => setShowQrModal(true)}
                                    className="p-2 rounded-3"
                                    style={{
                                        background: '#ffffff',
                                        border: '2px solid #f59e0b',
                                        cursor: 'pointer'
                                    }}
                                    title="Toca para ampliar el código QR"
                                >
                                    <QRCodeSVG
                                        value={
                                            participant.verificationToken ||
                                            participant.ticketFolio
                                        }
                                        size={82}
                                        bgColor="#ffffff"
                                        fgColor="#09090b"
                                        level="H"
                                        includeMargin={false}
                                        imageSettings={{
                                            src: '/img/favicon/Big.png',
                                            height: 20,
                                            width: 20,
                                            excavate: true
                                        }}
                                    />
                                </div>
                                <div>
                                    <div
                                        className="text-uppercase fw-bold"
                                        style={{ color: '#fde68a', fontSize: '0.68rem', letterSpacing: 0.8 }}
                                    >
                                        🎫 Tu Pase de Acceso y Sellos
                                    </div>
                                    <div
                                        className="fw-black text-white"
                                        style={{
                                            fontFamily: 'monospace',
                                            fontSize: '1.2rem',
                                            fontWeight: 900
                                        }}
                                    >
                                        {participant.ticketFolio}
                                    </div>
                                    <div style={{ color: '#d4d4d8', fontSize: '0.78rem' }}>
                                        {participant.fullName} • Tómbola #{participant.raffleTicketCode}
                                    </div>
                                </div>
                            </div>

                            <button
                                type="button"
                                onClick={() => setShowQrModal(true)}
                                className="btn btn-sm fw-bold px-3 py-2"
                                style={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    color: '#09090b',
                                    border: 'none',
                                    borderRadius: 8,
                                    fontSize: '0.8rem'
                                }}
                            >
                                <i className="bi bi-arrows-fullscreen me-1"></i>
                                Ver QR
                            </button>
                        </div>

                        {/* SECCIÓN SUPERIOR: RECOMPENSA DESBLOQUEADA (CUPÓN DORADO O INSIGNIA DIGITAL) */}
                        {participant.hasEarnedReward && (
                            <motion.div
                                initial={{ scale: 0.94, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="rounded-4 p-4 mb-4 text-center"
                                style={{
                                    background:
                                        'linear-gradient(155deg, rgba(180, 83, 9, 0.35) 0%, rgba(24, 24, 27, 0.98) 55%, rgba(120, 53, 15, 0.4) 100%)',
                                    border: '2px solid #f59e0b',
                                    boxShadow:
                                        '0 20px 50px rgba(0,0,0,0.85), 0 0 35px rgba(245, 158, 11, 0.3)'
                                }}
                            >
                                <span
                                    className="badge rounded-pill px-3 py-2 mb-2"
                                    style={{
                                        background: 'linear-gradient(90deg, #f59e0b 0%, #fbbf24 100%)',
                                        color: '#09090b',
                                        fontWeight: 900,
                                        fontSize: '0.76rem',
                                        letterSpacing: '0.8px'
                                    }}
                                >
                                    🏆 ¡META COMPLETADA — CUPÓN DORADO DESBLOQUEADO!
                                </span>

                                <h3
                                    className="fw-black text-uppercase mt-2 mb-1"
                                    style={{ color: '#fde68a', fontWeight: 900, fontSize: '1.35rem' }}
                                >
                                    {config?.rewardPrizeTitle ||
                                        'Vaso Conmemorativo Oficial Xolo Fest'}
                                </h3>
                                <p style={{ color: '#e4e4e7', fontSize: '0.84rem' }}>
                                    Presenta este Cupón Dorado en el Módulo de Recompensas para
                                    recibir tu souvenir físico oficial.
                                </p>

                                <div
                                    className="p-3 rounded-4 mb-3 mx-auto"
                                    style={{
                                        maxWidth: '340px',
                                        background:
                                            'linear-gradient(135deg, #b45309 0%, #f59e0b 50%, #b45309 100%)',
                                        color: '#09090b',
                                        boxShadow: '0 8px 25px rgba(245, 158, 11, 0.35)'
                                    }}
                                >
                                    <div
                                        className="text-uppercase fw-bold"
                                        style={{ fontSize: '0.68rem', letterSpacing: '1px' }}
                                    >
                                        FOLIO OFICIAL DE CANJE
                                    </div>
                                    <div
                                        style={{
                                            fontFamily: "'Courier New', monospace",
                                            fontSize: '1.75rem',
                                            fontWeight: 900,
                                            color: '#ffffff',
                                            textShadow: '0 2px 6px rgba(0,0,0,0.45)'
                                        }}
                                    >
                                        #{String(participant.rewardCouponCode || 'CANJE-XOLO').replace(/^#/, '')}
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <div
                                        onClick={() => setShowQrModal(true)}
                                        className="d-inline-block p-3 rounded-4"
                                        style={{
                                            background: '#ffffff',
                                            border: '4px solid #f59e0b',
                                            cursor: 'pointer'
                                        }}
                                        title="Toca para ampliar el código QR"
                                    >
                                        <QRCodeSVG
                                            value={
                                                participant.rewardCouponCode ||
                                                participant.verificationToken ||
                                                participant.ticketFolio
                                            }
                                            size={180}
                                            bgColor="#ffffff"
                                            fgColor="#09090b"
                                            level="H"
                                            includeMargin={false}
                                            imageSettings={{
                                                src: '/img/favicon/Big.png',
                                                height: 40,
                                                width: 40,
                                                excavate: true
                                            }}
                                        />
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    disabled={downloadingPng}
                                    onClick={() => handleDownloadCard('GOLDEN_COUPON')}
                                    className="btn btn-lg fw-bold w-100 py-3 rounded-3"
                                    style={{
                                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                        color: '#09090b',
                                        border: 'none',
                                        boxShadow: '0 8px 25px rgba(245, 158, 11, 0.4)',
                                        fontSize: '0.96rem'
                                    }}
                                >
                                    {downloadingPng
                                        ? 'Generando Cupón PNG...'
                                        : '📥 Descargar Cupón Dorado (PNG)'}
                                </button>
                            </motion.div>
                        )}

                        {!participant.hasEarnedReward && participant.receivedDigitalBadge && (
                            <motion.div
                                initial={{ scale: 0.94, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                className="rounded-4 p-4 mb-4 text-center"
                                style={{
                                    background:
                                        'linear-gradient(160deg, rgba(127, 29, 29, 0.5) 0%, rgba(9, 9, 11, 0.98) 60%, rgba(180, 83, 9, 0.3) 100%)',
                                    border: '2px solid #dc2626',
                                    boxShadow:
                                        '0 20px 50px rgba(0,0,0,0.85), 0 0 35px rgba(220, 38, 38, 0.35)'
                                }}
                            >
                                <span
                                    className="badge rounded-pill px-3 py-2 mb-2"
                                    style={{
                                        background: 'linear-gradient(90deg, #dc2626 0%, #f59e0b 100%)',
                                        color: '#ffffff',
                                        fontWeight: 900,
                                        fontSize: '0.75rem',
                                        letterSpacing: '0.8px'
                                    }}
                                >
                                    🎖️ EDICIÓN ESPECIAL — FAN LEYENDA XOLOS 2026
                                </span>

                                {/* Emblema Dorado Vectorial */}
                                <div className="my-3">
                                    <div
                                        className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                        style={{
                                            width: '92px',
                                            height: '92px',
                                            background:
                                                'radial-gradient(circle, #dc2626 0%, #450a0a 100%)',
                                            border: '3px solid #f59e0b',
                                            boxShadow: '0 0 25px rgba(245, 158, 11, 0.5)'
                                        }}
                                    >
                                        <i
                                            className="bi bi-trophy-fill text-warning"
                                            style={{ fontSize: '2.6rem' }}
                                        ></i>
                                    </div>
                                </div>

                                <h3
                                    className="fw-black text-uppercase mb-2"
                                    style={{ color: '#fde68a', fontWeight: 900, fontSize: '1.35rem' }}
                                >
                                    {config?.digitalBadgeTitle || 'Insignia Leyenda Xolos 2026'}
                                </h3>

                                <div
                                    className="p-3 rounded-3 mb-3"
                                    style={{
                                        background: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(245, 158, 11, 0.4)',
                                        color: '#f4f4f5',
                                        fontSize: '0.86rem',
                                        lineHeight: 1.5
                                    }}
                                >
                                    ¡Felicidades! Has completado el circuito Xolo Fest. Los souvenirs
                                    físicos del día se han agotado, pero has desbloqueado tu{' '}
                                    <strong style={{ color: '#fde68a' }}>
                                        Insignia Digital Exclusiva
                                    </strong>
                                    .
                                </div>

                                <button
                                    type="button"
                                    disabled={downloadingPng}
                                    onClick={() => handleDownloadCard('DIGITAL_BADGE')}
                                    className="btn btn-lg fw-bold w-100 py-3 rounded-3"
                                    style={{
                                        background: 'linear-gradient(135deg, #dc2626 0%, #f59e0b 100%)',
                                        color: '#ffffff',
                                        border: 'none',
                                        boxShadow: '0 8px 25px rgba(220, 38, 38, 0.45)',
                                        fontSize: '0.96rem'
                                    }}
                                >
                                    {downloadingPng
                                        ? 'Generando Insignia PNG...'
                                        : '📥 Descargar Insignia Conmemorativa (PNG)'}
                                </button>
                            </motion.div>
                        )}

                        {/* Alerta Ámbar si ya alcanzó la meta numérica pero le falta el patrocinador obligatorio */}
                        {missingMandatorySponsor && (
                            <div
                                className="p-3 rounded-4 mb-3 d-flex align-items-start gap-2"
                                style={{
                                    background: 'rgba(245, 158, 11, 0.16)',
                                    border: '2px solid #f59e0b',
                                    color: '#fde68a'
                                }}
                            >
                                <i className="bi bi-bullseye fs-4 text-warning"></i>
                                <div style={{ fontSize: '0.84rem' }}>
                                    <div className="fw-bold text-uppercase mb-1">
                                        ¡Estás a 1 paso de desbloquear tu recompensa!
                                    </div>
                                    <span>
                                        Ya acumulaste {uniqueStampsCount} sellos, pero la regla actual
                                        requiere visitar al menos{' '}
                                        <strong>1 Activación de Patrocinador</strong> (
                                        <em>Tiro a Gol Caliente.mx</em> o <em>Reto Nissan</em>).
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Cuadrícula Interactiva de Estaciones / Sellos */}
                        <div
                            className="rounded-4 p-4 mb-3"
                            style={{
                                background: 'rgba(24, 24, 27, 0.95)',
                                border: '1px solid rgba(255, 255, 255, 0.14)'
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-between mb-3">
                                <h5
                                    className="mb-0 fw-bold text-white"
                                    style={{ fontSize: '1.02rem' }}
                                >
                                    <i className="bi bi-grid-fill text-warning me-2"></i>
                                    Mapa de Sellos del Festival
                                </h5>
                                <span style={{ color: '#a1a1aa', fontSize: '0.74rem' }}>
                                    1 sello por estación única
                                </span>
                            </div>

                            <div className="row g-2">
                                {availableStations.map((st, idx) => {
                                    const isVisited = Boolean(st.isVisited);
                                    const isSponsor = st.stationType === 'ACTIVATION';
                                    return (
                                        <div className="col-6" key={st.stationCode || idx}>
                                            <motion.div
                                                whileHover={{ scale: 1.01 }}
                                                className="p-3 rounded-4 h-100 d-flex flex-column justify-content-between position-relative"
                                                style={{
                                                    background: isVisited
                                                        ? 'linear-gradient(145deg, rgba(16, 185, 129, 0.18) 0%, rgba(245, 158, 11, 0.22) 100%)'
                                                        : 'rgba(9, 9, 11, 0.75)',
                                                    border: isVisited
                                                        ? '2px solid #f59e0b'
                                                        : '1px dashed #3f3f46',
                                                    minHeight: '122px'
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-start mb-2">
                                                    <span
                                                        className="badge rounded-pill"
                                                        style={{
                                                            background: isSponsor
                                                                ? 'rgba(220, 38, 38, 0.25)'
                                                                : 'rgba(255, 255, 255, 0.08)',
                                                            color: isSponsor ? '#fca5a5' : '#a1a1aa',
                                                            fontSize: '0.62rem',
                                                            fontWeight: 800
                                                        }}
                                                    >
                                                        {st.stationType === 'ACCESS'
                                                            ? 'ACCESO'
                                                            : st.stationType === 'ACTIVATION'
                                                            ? 'PATROCINADOR'
                                                            : 'GASTRO / POS'}
                                                    </span>

                                                    {isVisited ? (
                                                        <i className="bi bi-patch-check-fill text-warning fs-5"></i>
                                                    ) : (
                                                        <i className="bi bi-circle text-secondary"></i>
                                                    )}
                                                </div>

                                                <div>
                                                    <div
                                                        className="fw-bold"
                                                        style={{
                                                            fontSize: '0.84rem',
                                                            color: isVisited ? '#ffffff' : '#a1a1aa'
                                                        }}
                                                    >
                                                        {st.stationName}
                                                    </div>
                                                    {st.sponsorName && (
                                                        <div
                                                            style={{
                                                                color: '#f59e0b',
                                                                fontSize: '0.68rem',
                                                                fontWeight: 700
                                                            }}
                                                        >
                                                            {st.sponsorName}
                                                        </div>
                                                    )}
                                                </div>

                                                <div className="mt-2 pt-1 border-top border-secondary border-opacity-25 d-flex justify-content-between align-items-center">
                                                    <small
                                                        style={{
                                                            fontSize: '0.68rem',
                                                            color: isVisited ? '#6ee7b7' : '#71717a',
                                                            fontWeight: 700
                                                        }}
                                                    >
                                                        {isVisited
                                                            ? `SELLADO (${st.visitsCount || 1}x)`
                                                            : 'Por visitar'}
                                                    </small>
                                                    {isVisited && st.lastVisitedAtUtc && (
                                                        <small
                                                            style={{
                                                                fontSize: '0.64rem',
                                                                color: '#fde68a',
                                                                fontFamily: 'monospace'
                                                            }}
                                                        >
                                                            {formatTimeShort(st.lastVisitedAtUtc)}
                                                        </small>
                                                    )}
                                                </div>
                                            </motion.div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Barra de Progreso de Estaciones Únicas y Stock Disponible */}
                        <div
                            className="rounded-4 p-4 mb-3"
                            style={{
                                background: 'rgba(24, 24, 27, 0.95)',
                                border: '1px solid rgba(245, 158, 11, 0.4)',
                                boxShadow: '0 15px 35px rgba(0,0,0,0.7)'
                            }}
                        >
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                <div>
                                    <span
                                        className="text-uppercase fw-bold d-block"
                                        style={{
                                            color: '#fde68a',
                                            fontSize: '0.73rem',
                                            letterSpacing: '0.8px'
                                        }}
                                    >
                                        PROGRESO DE TU PASAPORTE GAMIFICADO
                                    </span>
                                    <h4 className="fw-black text-white mb-0" style={{ fontWeight: 900 }}>
                                        {uniqueStampsCount} / {rewardGoal} Estaciones Visitadas
                                    </h4>
                                </div>

                                <span
                                    className="badge rounded-pill px-3 py-2"
                                    style={{
                                        background:
                                            remainingStock > 0
                                                ? 'rgba(16, 185, 129, 0.16)'
                                                : 'rgba(220, 38, 38, 0.2)',
                                        border:
                                            remainingStock > 0
                                                ? '1px solid #10b981'
                                                : '1px solid #dc2626',
                                        color: remainingStock > 0 ? '#6ee7b7' : '#fca5a5',
                                        fontSize: '0.72rem',
                                        fontWeight: 800
                                    }}
                                >
                                    {remainingStock > 0
                                        ? `🎁 Stock Souvenirs: ${remainingStock} / ${stockLimit}`
                                        : '🎖️ Souvenirs Agotados (Insignia Activa)'}
                                </span>
                            </div>

                            {/* Barra visual animada */}
                            <div
                                className="progress rounded-pill mb-2"
                                style={{
                                    height: '14px',
                                    background: '#09090b',
                                    border: '1px solid #3f3f46'
                                }}
                            >
                                <div
                                    className="progress-bar progress-bar-striped progress-bar-animated"
                                    role="progressbar"
                                    style={{
                                        width: `${progressPercent}%`,
                                        background:
                                            'linear-gradient(90deg, #dc2626 0%, #f59e0b 100%)'
                                    }}
                                    aria-valuenow={progressPercent}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                />
                            </div>

                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <small style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>
                                    Premio Meta:{' '}
                                    <strong className="text-white">
                                        {config?.rewardPrizeTitle ||
                                            'Vaso Conmemorativo Oficial Xolo Fest'}
                                    </strong>
                                </small>

                                <span
                                    className="badge"
                                    style={{
                                        background:
                                            unlockRule === 'MANDATORY_ACTIVATION'
                                                ? 'rgba(220, 38, 38, 0.2)'
                                                : 'rgba(255, 255, 255, 0.08)',
                                        color:
                                            unlockRule === 'MANDATORY_ACTIVATION'
                                                ? '#fca5a5'
                                                : '#d4d4d8',
                                        fontSize: '0.68rem'
                                    }}
                                >
                                    {unlockRule === 'MANDATORY_ACTIVATION'
                                        ? `🎯 Requiere 1 Patrocinador (${
                                              hasSponsorStamp ? 'Cumplido ✓' : 'Pendiente'
                                          })`
                                        : '🔓 Regla: Cualquier combinación de estaciones'}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default XoloFestPassport;
